import { useFlag } from '@unleash/proxy-client-react';
import { AxiosResponse } from 'axios';

import { format } from 'date-fns';
import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { isTestOrLocalEnvironment } from 'Api/documents/documents.helpers';
import { GValueDto } from 'Components/InsuranceDetailsSections/insuranceDetailsSections.types';
import { filterCorporate } from 'Components/InsuranceOverviewSection/insuranceOverviewSection.helpers';
import { InsuranceStatus } from 'Constants/insuranceStatus.constants';
import { ProductName } from 'Constants/products.constants';
import { ProductInsuranceCancelRequest } from 'Forms/CancelFormV2/cancelForm.types';
import {
  ProductInsuranceChangeRequest,
  ProductPriceRequest,
} from 'Forms/ChangeForm/changeForm.types';
import { AutomaticCancelInsuranceFormValues } from 'Pages/CancelInsurancePage/cancelInsurancePage.types';
import { getAppText } from 'Utils/text';
import { isDefined } from 'Utils/type.utils';
import { getStbClient } from '../../features/network';
import {
  getCancelCWPUrlByPillar,
  getCancelUrlByPillar,
  getChangeUrlByPillar,
  getDetailsUrl,
  getManualCancelUrlByPillar,
  getNewPriceUrl,
  getReregistrationAcceptUrl,
  getReregistrationPriceUrl,
  NAV_G_VALUE_URL,
} from './helpers/insurance.resourceMapping.helpers';
import {
  OrganizationPillarTypes,
  POLICY_NUMBER_ID,
} from './insurance.constants';
import { mapToInsuranceDetails } from './mappers/mapToInsuranceDetails';
import {
  mapf2100ProductToInsuranceOverview,
  mapToInsuranceOverview,
} from './mappers/mapToInsuranceOverview';
import {
  InsuranceCancelRequestDto,
  InsuranceChangeRequestDto,
  InsuranceDetailsDto,
  InsuranceDto,
  InsuranceManualChangeRequestDto,
  InsuranceOverviewDto,
  PolicyOverviewDto,
  PriceDto,
  RegistrationPriceDto,
} from './types/insurance.dto.types';
import {
  CorporateInsuranceOverviewResponse,
  InsuranceDetailsOnTodaysDate,
  InsuranceDetailsWithPotentialChanges,
  InsuranceOverviewResponse,
} from './types/insurance.responses.types';
import { InsuranceDetails, InsuranceOverview } from './types/insurance.types';
import { StatusCwp } from './types/insurance.types.cwp';

export function useCorporateInsuranceOverview(
  insuranceType: string,
): CorporateInsuranceOverviewResponse {
  const { data, allFailed, someFailed, loading } = useInsuranceOverviewList();

  const corporateInsurances = filterCorporate(data);
  const filteredInsurances = corporateInsurances.filter(
    (i) => i.type === insuranceType,
  );

  return {
    insurances: filteredInsurances,
    error: allFailed || (someFailed && !loading && !filteredInsurances.length),
    loading,
  };
}

export function useInsuranceOverview(
  policyNumber: string,
): InsuranceOverviewResponse {
  const { data, allFailed, someFailed, loading } = useInsuranceOverviewList();

  const insurance = data.find((i) => i.policyNumber === policyNumber);

  return {
    insurance,
    error: allFailed || (someFailed && !loading && !insurance),
    loading: !insurance && loading,
  };
}

export function useInsuranceOverviewList(): {
  data: InsuranceOverview[];
  loading: boolean;
  allFailed: boolean;
  someFailed: boolean;
  allLoading: boolean;
} {
  const {
    data: riskData,
    error: riskError,
    isLoading: isLoadingRisk,
  } = useQuery({
    queryKey: ['riskContracts'],
    queryFn: () => getPolicies('/insurance/policies'),
    refetchOnWindowFocus: false,
    retry: 3,
  });

  const {
    data: healthData,
    error: healthError,
    isLoading: isLoadinghealth,
  } = useQuery({
    queryKey: ['healthContracts'],
    queryFn: () => getContracts('/insurance/health/engagement/contracts'),
    refetchOnWindowFocus: false,
    retry: 3,
  });

  const {
    data: lifeData,
    error: lifeError,
    isLoading: isLoadingLife,
  } = useQuery({
    queryKey: ['lifeContracts'],
    queryFn: () => getContracts('/insurance/life/engagement/contracts/v2'),
    refetchOnWindowFocus: false,
    retry: 3,
  });

  const {
    data: lifePensionFundsData,
    error: lifePensionFundsError,
    isLoading: isLoadinglifePensionFunds,
  } = useQuery({
    queryKey: ['lifePensionFundContracts'],
    queryFn: () =>
      getContracts(
        '/insurance/life/engagement/contracts/pensionfunds/insurance',
      ),
    refetchOnWindowFocus: false,
    retry: 3,
  });

  const data = useMemo(() => {
    return [riskData, healthData, lifeData, lifePensionFundsData]
      .filter<InsuranceOverview[]>(isDefined)
      .flat();
  }, [healthData, lifeData, lifePensionFundsData, riskData]);

  const someFailed =
    !!riskError || !!healthError || !!lifeError || !!lifePensionFundsError;

  const allFailed =
    !!riskError && !!healthError && !!lifeError && !!lifePensionFundsError;

  const loading =
    isLoadingRisk ||
    isLoadinghealth ||
    isLoadingLife ||
    isLoadinglifePensionFunds;

  const allLoading =
    isLoadingRisk &&
    isLoadinghealth &&
    isLoadingLife &&
    isLoadinglifePensionFunds;

  return {
    data,
    loading,
    allFailed,
    someFailed,
    allLoading,
  };
}

async function getContracts(endpoint: string): Promise<InsuranceOverview[]> {
  const client = await getStbClient();

  return client
    .get<Array<InsuranceOverviewDto>>(endpoint)
    .then((res) => mapToInsuranceOverview(res.data));
}

async function getPolicies(endpoint: string): Promise<InsuranceOverview[]> {
  const client = await getStbClient();

  return client
    .get<Array<PolicyOverviewDto>>(endpoint)
    .then((res) => mapf2100ProductToInsuranceOverview(res.data));
}

export function useVehicleInsuranceDetails(licensePlateNumber: string): {
  data: InsuranceOverview | undefined;
  loading: boolean;
  error: boolean;
} {
  const { data, loading, someFailed } = useInsuranceOverviewList();

  const vehicles = data.filter(
    (insurance) => insurance.insuranceType === ProductName.CAR,
  );
  const vehicle = vehicles?.find((insurance) =>
    insurance.coverages.find((coverage) =>
      coverage.insuredName.includes(licensePlateNumber),
    ),
  );

  return {
    data: vehicle,
    loading: loading && !vehicle,
    error: !vehicle && !loading && someFailed,
  };
}

export function useInsuranceDetailsWithPotentialChanges(
  policyNumber: string,
): InsuranceDetailsWithPotentialChanges {
  const {
    insurance,
    loading: insuranceOverviewLoading,
    error: insuranceOverviewError,
  } = useInsuranceOverview(policyNumber);

  const organizationPillar = insurance?.organizationPillar;

  const { data, error, isLoading } = useQuery({
    queryKey: ['insuranceDetailsWithPotentialChanges', policyNumber],
    queryFn: () => {
      if (
        organizationPillar !== OrganizationPillarTypes.LIFE &&
        organizationPillar !== OrganizationPillarTypes.RISK_INSURANCE
      ) {
        return null;
      }

      return getInsuranceDetails(policyNumber, organizationPillar);
    },
    enabled:
      isDefined(insurance) ||
      organizationPillar === OrganizationPillarTypes.LIFE ||
      organizationPillar === OrganizationPillarTypes.RISK_INSURANCE,
    refetchOnWindowFocus: false,
    retry: 3,
    refetchOnMount: true,
  });

  return {
    insuranceDetailsWithPotentialChanges: data ?? null,
    loading: insuranceOverviewLoading || isLoading,
    error: insuranceOverviewError || !!error,
  };
}

export function useInsuranceDetailsOnTodaysDate(
  policyNumber: string,
): InsuranceDetailsOnTodaysDate {
  const {
    insurance,
    loading: insuranceOverviewLoading,
    error: insuranceOverviewError,
  } = useInsuranceOverview(policyNumber);

  const getF2100PoliciesFromCwpToggle = useFlag('CLI-1685-overview-from-cwp');
  const organizationPillar = insurance?.organizationPillar;
  const insuranceStatus = getF2100PoliciesFromCwpToggle
    ? insurance?.status
    : insurance?.insuranceStatus;

  const { data, error, isLoading } = useQuery({
    queryKey: ['insuranceDetailsOnTodaysDate', policyNumber],
    queryFn: () => {
      if (
        organizationPillar === OrganizationPillarTypes.HEALTH ||
        insuranceStatus === StatusCwp.UNDER_ACTIVATION ||
        insuranceStatus === InsuranceStatus.UNDER_ACTIVATION
      ) {
        return null;
      }

      if (organizationPillar === OrganizationPillarTypes.LIFE) {
        return getLifeInsuranceDetailsOnCurrentDate(policyNumber);
      }

      return getRiskInsuranceDetailsOnCurrentDate(policyNumber);
    },
    enabled:
      isDefined(insurance) ||
      organizationPillar === OrganizationPillarTypes.LIFE ||
      organizationPillar === OrganizationPillarTypes.RISK_INSURANCE,
    refetchOnWindowFocus: false,
    retry: 3,
    refetchOnMount: true,
  });

  return {
    insuranceDetailsOnTodaysDate: data ?? null,
    loading: insuranceOverviewLoading || isLoading,
    error: insuranceOverviewError || error != null,
  };
}

export function useGValue(): {
  gValue: string | undefined;
  loading: boolean;
  error: Error | null;
} {
  const { data, error, isLoading } = useQuery({
    queryKey: ['gValue'],
    queryFn: () => getGValue(),
    refetchOnWindowFocus: false,
    retry: 3,
  });
  return { gValue: data, loading: isLoading, error };
}

async function getInsuranceDetails<
  T extends InsuranceDetailsDto = InsuranceDetailsDto,
>(
  policyNumber: string,
  organizationPillar?: Exclude<OrganizationPillarTypes, 'HEALTH'>,
): Promise<InsuranceDetails> {
  if (!organizationPillar) {
    return Promise.reject(Error('Organization pillar not provided'));
  }

  const client = await getStbClient();
  const url = getDetailsUrl(policyNumber, organizationPillar);

  const response = await client.get<InsuranceDto<T>>(url);
  return mapToInsuranceDetails(response.data);
}

/* date should be on format: yyyy-MM-dd ? */
export async function getRiskInsuranceDetailsOnCurrentDate<
  T extends InsuranceDetailsDto = InsuranceDetailsDto,
>(policyNumber: string): Promise<InsuranceDetails> {
  const client = await getStbClient();
  const currentDate = format(new Date(), 'yyyy-MM-dd');
  const response = await client.get<InsuranceDto<T>>(
    `/insurance/risk/engagement/contracts/${policyNumber}/withDetailsOnDate?date=${currentDate}&details=true`,
  );
  return mapToInsuranceDetails(response.data);
}

export async function getLifeInsuranceDetailsOnCurrentDate<
  T extends InsuranceDetailsDto = InsuranceDetailsDto,
>(policyNumber: string): Promise<InsuranceDetails> {
  const client = await getStbClient();
  const currentDate = format(new Date(), 'yyyy-MM-dd');
  const response = await client.get<InsuranceDto<T>>(
    `/insurance/life/engagement/contracts/${policyNumber}/withDetailsOnDate?date=${currentDate}&details=true`,
  );
  return mapToInsuranceDetails(response.data);
}

export async function postManualInsuranceChangeRequest(
  message: string,
  organizationPillar: OrganizationPillarTypes,
  policyNumber: string,
): Promise<AxiosResponse<InsuranceManualChangeRequestDto>> {
  const baseUrl = getChangeUrlByPillar(organizationPillar).replace(
    POLICY_NUMBER_ID,
    policyNumber,
  );
  const url = `${baseUrl}/manual`;
  const client = await getStbClient();

  return client.post<InsuranceManualChangeRequestDto>(url, {
    changeMessage: message,
    insuranceId: policyNumber,
    sendCustomerConfirmation: true,
  });
}

async function getGValue(): Promise<string> {
  const client = await getStbClient();
  const response = await client.get<GValueDto>(NAV_G_VALUE_URL);

  return response.data.amount;
}

export async function fetchCalculatedPrice(
  insurance: InsuranceDetails,
  newValues: ProductPriceRequest,
  abortSignal?: AbortSignal,
): Promise<PriceDto> {
  const { policyNumber, organizationPillar, type } = insurance;

  const client = await getStbClient();
  const url = getNewPriceUrl(policyNumber, organizationPillar);

  const timeout = isTestOrLocalEnvironment() ? 70000 : 50000;

  const { data }: { data: PriceDto } = await client.post(
    url,
    {
      id: policyNumber,
      insurance: { ...newValues, type },
      sendCustomerConfirmation: true,
    },
    { signal: abortSignal, timeout },
  );

  return {
    monthlyPrice: data.monthlyPrice,
    yearlyPrice: data.yearlyPrice,
  };
}

export async function getRegistrationPrice(
  insurance: InsuranceDetails,
): Promise<RegistrationPriceDto> {
  const { policyNumber, organizationPillar } = insurance;

  const client = await getStbClient();
  const url = getReregistrationPriceUrl(policyNumber, organizationPillar);

  const { data }: { data: PriceDto } = await client.post(url, {
    id: policyNumber,
    sendCustomerConfirmation: true,
  });

  return {
    monthly: data.monthlyPrice,
    annual: data.yearlyPrice,
  };
}

export async function reRegisterInsurance(
  insurance: InsuranceDetails,
): Promise<AxiosResponse> {
  const { policyNumber, organizationPillar } = insurance;

  const client = await getStbClient();
  const url = getReregistrationAcceptUrl(policyNumber, organizationPillar);

  return client.post(url, {
    id: policyNumber,
    sendCustomerConfirmation: true,
  });
}

export async function postInsuranceChange(
  values: ProductInsuranceChangeRequest,
  insurance: InsuranceDetails,
): Promise<AxiosResponse> {
  const { organizationPillar, policyNumber, type } = insurance;

  const url = getChangeUrlByPillar(organizationPillar).replace(
    POLICY_NUMBER_ID,
    policyNumber,
  );

  const client = await getStbClient();

  const timeout = isTestOrLocalEnvironment() ? 80000 : 60000;

  return client.post<InsuranceChangeRequestDto>(
    url,
    {
      insurance: { ...values, type },
      sendCustomerConfirmation: true,
    },
    { timeout },
  );
}

export async function postAutomaticInsuranceCancellation(
  organizationPillar: OrganizationPillarTypes,
  policyNumber: string,
  formValues: AutomaticCancelInsuranceFormValues,
): Promise<AxiosResponse> {
  const url = getCancelUrlByPillar(organizationPillar).replace(
    POLICY_NUMBER_ID,
    policyNumber,
  );

  const client = await getStbClient();
  return client.post(url, formValues);
}

export async function postCancellationCwp(
  insurance: InsuranceDetails,
  values: ProductInsuranceCancelRequest,
): Promise<AxiosResponse> {
  const { organizationPillar, policyNumber } = insurance;
  const url = getCancelCWPUrlByPillar(organizationPillar).replace(
    POLICY_NUMBER_ID,
    policyNumber,
  );

  const client = await getStbClient();
  return client.post<InsuranceCancelRequestDto>(url, values);
}

export async function postManualInsuranceCancelRequest(
  message: string,
  organizationPillar: OrganizationPillarTypes,
  policyNumber: string,
): Promise<AxiosResponse> {
  const url = getManualCancelUrlByPillar(organizationPillar).replace(
    POLICY_NUMBER_ID,
    policyNumber,
  );

  const client = await getStbClient();

  return client.post(url, {
    changeMessage: `${getAppText(
      'cancelInsurance.alert.manualMessage.messagePrefix',
    )} ${message}`,
    insuranceId: policyNumber,
    sendCustomerConfirmation: true,
  });
}
